import {
  Datagrid,
  FunctionField,
  List,
  ListContextProvider,
  useGetMany,
  useRecordContext,
  WrapperField,
} from "react-admin";
import { CircularProgress, IconButton } from "@mui/material";
import { listContextFromArray } from "../../utils/listContextFromArray";
import { Student } from "../../providers/studentsProvider";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { EmptyState } from "../../misc/EmptyState";
import DeleteIcon from "@mui/icons-material/Delete";
import { cloneElement, ReactElement, useState } from "react";
import { serverAPI } from "../../api/server.api";
import { BKFCourse, Course } from "../../providers/coursesProvider";
import { renderAvatarAndName } from "../../lists/StudentsDataGrid";

export function CourseAttendeesList(props: { course: Course }) {
  const { course } = props;
  if (course.isBKFCourse) {
    return <BKFCourseParticipantsList course={course} />;
  } else {
    return <TheoryOrASFCOurseAttendeesList course={course} />;
  }
}

function BKFCourseParticipantsList(props: { course: BKFCourse }) {
  const { course } = props;
  return (
    <List
      resource={"courseParticipants"}
      title=" " // <-- prevent that the default list title is rendered
      filter={{ courseId: course.id }}
      sort={{ field: "name", order: "ASC" }}
      storeKey={`CourseParticipants@${course.id}`}
      actions={false}
      exporter={false}
      hasCreate={false}
      empty={
        <EmptyState
          noEntriesText="Noch keine Teilnehmer im Kurs."
          noMatchingEntriesText="Noch keine Teilnehmer im Kurs."
        />
      }
    >
      <Datagrid
        bulkActionButtons={false}
        rowClick={(id) => `/students/${id}`}
        empty={
          <EmptyState
            noEntriesText="Noch keine Teilnehmer im Kurs."
            noMatchingEntriesText="Noch keine Teilnehmer im Kurs."
          />
        }
      >
        <FunctionField label="Name" source="name" render={renderAvatarAndName} />
        <WrapperField label="" source="small" /* ... pseudo source which generates the CSS class: "column-small" */>
          <_DeleteButton course={course} />
        </WrapperField>
      </Datagrid>
    </List>
  );
}

function TheoryOrASFCOurseAttendeesList(props: { course: Course }) {
  const { course } = props;
  const { data: students } = useGetMany<Student>("students", { ids: Object.keys(course.attendees) });

  if (!students) {
    return <LoadingIndicator />;
  }

  return (
    <ListContextProvider value={listContextFromArray(students, { sort: { field: "name", order: "ASC" } })}>
      <Datagrid bulkActionButtons={false} rowClick={(id) => `/students/${id}`}>
        <_StyleAttendeeWrapper label="Name" course={course}>
          <FunctionField label="Name" source="name" render={renderAvatarAndName} />
        </_StyleAttendeeWrapper>
        <_StyleAttendeeWrapper label="gebuchte Termine" course={course}>
          {/* @ts-expect-error TS2589 - excessive type instantiation depth in react-admin's FunctionField */}
          <FunctionField
            render={({ id }: Student) => `${course.attendees[id].numBookedAppointments}/${course.numEvents}`}
          />
        </_StyleAttendeeWrapper>
        <WrapperField label="" source="small" /* ... pseudo source which generates the CSS class: "column-small" */>
          <_DeleteButton course={course} />
        </WrapperField>
      </Datagrid>
    </ListContextProvider>
  );
}

function _StyleAttendeeWrapper({ course, children }: { label: string; course: Course; children: ReactElement }) {
  const student = useRecordContext<Student>();

  if (!student) {
    return null;
  }

  if (!course.attendees[student.id]?.hasBookedUpcomingAppointment) {
    return cloneElement(children, { sx: { color: "text.secondary" } });
  } else {
    return children;
  }
}

function _DeleteButton({ course }: { course: Course }) {
  const student = useRecordContext<Student>();
  const [removingStudent, setRemovingStudent] = useState(false);

  if (!student) {
    return null;
  }

  const removeStudentFromCourse = async () => {
    try {
      setRemovingStudent(true);
      await serverAPI.removeStudentFromCourse({ student, course });
      showSuccessToast(`${student.name} erfolgreich entfernt.`);
    } catch (error) {
      console.error(`Failed to remove student ${student.id} from course ${course.id}`, error);
      showErrorToast(`Fehler beim Entfernen von ${student.name}.`);
    } finally {
      setRemovingStudent(false);
    }
  };

  return removingStudent ? (
    <IconButton disabled>
      <CircularProgress size={24} />
    </IconButton>
  ) : course.attendees[student.id]?.hasBookedUpcomingAppointment ? (
    <IconButton
      onClick={(event) => {
        void removeStudentFromCourse();
        event.stopPropagation();
        event.preventDefault();
        return false;
      }}
    >
      <DeleteIcon />
    </IconButton>
  ) : null;
}
